'use client';

import { createSelectListCollection, Select } from '../../components/select';
import { Dialog } from '../../components/dialog';
import { Field } from '../../components/field';
import { Input } from '../../components/input';
import { Textarea } from '../../components/textarea';
import { toasterApi } from '../toast/ToastProvider';
import { VStack } from '@terra/ui-styled-system';
import { Tick01Icon, ArrowUpDownIcon } from '@terra/ui-icons';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { InstructionCategorySchema } from '@terra/shared-schemas-orchestrator';
import { ApplyModeFields } from './ApplyModeFields';
import { EndpointGlobsField } from './EndpointGlobsField';
import { resolveSelectableInstructionCategories } from './instruction-categories';
import {
  TestingInstructionFormSchema,
  type TestingInstructionFormValues,
} from './testing-instruction-form.schema';
import type {
  TestingInstructionNote,
  TestingInstructionPipeline,
  TestingInstructionsTarget,
} from './types';

export type TestingInstructionFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: TestingInstructionsTarget;
  note: TestingInstructionNote | null;
  onSaved: (note: TestingInstructionNote) => void;
};
const EMPTY_FORM_VALUES: TestingInstructionFormValues = {
  title: '',
  content: '',
  applyMode: 'ALWAYS',
  pipelines: [],
  endpointGlobs: [],
  description: null,
  category: null,
};
const normalizePipelinesForForm = ({
  pipelines,
  availablePipelines,
}: {
  pipelines: TestingInstructionPipeline[];
  availablePipelines: TestingInstructionPipeline[];
}): TestingInstructionPipeline[] => {
  if (pipelines.length === 0) {
    return [];
  }

  const coversAllAvailable = availablePipelines.every((pipeline) =>
    pipelines.includes(pipeline),
  );

  return coversAllAvailable ? [] : pipelines;
};

export const TestingInstructionFormDialog = ({
  open,
  onOpenChange,
  target,
  note,
  onSaved,
}: TestingInstructionFormDialogProps) => {
  const t = useTranslations('designSystem.testingInstructions');
  const tCommon = useTranslations('designSystem.common');
  const { saveAsync, isPending } = target.useSaveNote();
  const [contentError, setContentError] = useState<string | undefined>();
  const isEditing = !!note;
  const form = useForm<TestingInstructionFormValues>({
    resolver: zodResolver(TestingInstructionFormSchema),
    defaultValues: EMPTY_FORM_VALUES,
  });
  const applyMode = useWatch({ control: form.control, name: 'applyMode' });
  const endpointGlobs = useWatch({
    control: form.control,
    name: 'endpointGlobs',
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      note
        ? {
            title: note.title,
            content: note.content,
            applyMode: note.applyMode,
            pipelines: normalizePipelinesForForm({
              pipelines: note.pipelines,
              availablePipelines: target.availablePipelines,
            }),
            endpointGlobs: note.endpointGlobs,
            description: note.description,
            category: note.category,
          }
        : EMPTY_FORM_VALUES,
    );
    setContentError(undefined);
  }, [open, note, form, target.availablePipelines]);
  const selectableCategories = useMemo(
    () =>
      resolveSelectableInstructionCategories({
        targetKind: target.targetKind,
        isDeepSecurityScanEnabled: target.isDeepSecurityScanEnabled ?? false,
      }),
    [target.isDeepSecurityScanEnabled, target.targetKind],
  );
  const categoryOptions = useMemo(
    () =>
      createSelectListCollection({
        items: selectableCategories.map((category) => ({
          label: t(`categories.${category}`),
          value: category,
        })),
      }),
    [t, selectableCategories],
  );
  const handleInvalidSave = () => {
    toasterApi.error({
      title: t('validationError.title'),
      description: tCommon('pleaseFixFormErrors'),
    });
  };

  const handleSave = form.handleSubmit(async (values) => {
    setContentError(undefined);
    const notePayload = {
      title: values.title.trim(),
      content: values.content.trim(),
      applyMode: values.applyMode,
      pipelines:
        values.applyMode === 'MANUAL' || values.applyMode === 'AGENT_REQUESTED'
          ? []
          : normalizePipelinesForForm({
              pipelines: values.pipelines,
              availablePipelines: target.availablePipelines,
            }),
      endpointGlobs:
        values.applyMode === 'AUTO_ATTACHED'
          ? values.endpointGlobs.map((glob) => glob.trim()).filter(Boolean)
          : [],
      description:
        values.applyMode === 'AGENT_REQUESTED'
          ? (values.description?.trim() ?? null)
          : null,
      category: values.category,
    };

    if (target.persistMode === 'deferred') {
      target.onDeferredSave?.(notePayload);
      onOpenChange(false);

      return;
    }

    try {
      const result = await saveAsync({
        noteId: note?.id,
        note: notePayload,
      });

      if (!result.success) {
        const contentIssue = result.validationErrors?.find(
          (error) => error.fieldType === 'content',
        );

        if (contentIssue) {
          setContentError(t('maliciousContent'));
        } else {
          toasterApi.error({
            title: t('errors.failedToSave'),
            description: result.message ?? t('errors.failedToSave'),
          });
        }

        return;
      }

      if (result.data) {
        toasterApi.success({
          title: isEditing ? t('success.updated') : t('success.created'),
          description: isEditing
            ? t('success.updatedDescription')
            : t('success.createdDescription'),
        });
        onSaved(result.data);
      }
    } catch {
      toasterApi.error({
        title: t('errors.failedToSave'),
        description: t('errors.failedToSave'),
      });
    }
  }, handleInvalidSave);
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      form.reset(EMPTY_FORM_VALUES);
      setContentError(undefined);
    }

    onOpenChange(next);
  };

  const showPipelines = applyMode === 'ALWAYS' || applyMode === 'AUTO_ATTACHED';
  const showEndpointGlobs = applyMode === 'AUTO_ATTACHED';
  const showDescription = applyMode === 'AGENT_REQUESTED';
  const primaryLabel = isPending
    ? isEditing
      ? t('loadingStates.saving')
      : t('loadingStates.adding')
    : isEditing
      ? t('buttons.saveChanges')
      : t('buttons.addInstruction');

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      size="md"
      title={isEditing ? t('editInstruction') : t('addNewInstruction')}
      description={t('description')}
      closeLabel={tCommon('close')}
      secondaryAction={{
        label: tCommon('cancel'),
        onClick: () => handleOpenChange(false),
      }}
      primaryAction={{
        label: primaryLabel,
        onClick: () => handleSave(),
        loading: isPending,
        disabled: isPending,
      }}
    >
      <VStack gap={4} alignItems="flex-start" w="full">
        <Field.Root w="full" invalid={!!form.formState.errors.title}>
          <Field.Label>{t('form.title')}</Field.Label>
          <Input
            {...form.register('title')}
            placeholder={t('form.titlePlaceholder')}
            size="sm"
            borderColor={
              form.formState.errors.title ? 'border.error' : undefined
            }
          />
          {form.formState.errors.title ? (
            <Field.ErrorText>
              {t('validationError.titleRequired')}
            </Field.ErrorText>
          ) : null}
        </Field.Root>

        <ApplyModeFields
          control={form.control}
          applyMode={applyMode ?? 'ALWAYS'}
          availablePipelines={target.availablePipelines}
          showPipelines={showPipelines}
        />

        {showEndpointGlobs ? (
          <EndpointGlobsField
            value={endpointGlobs ?? []}
            onChange={(next) =>
              form.setValue('endpointGlobs', next, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            errorMessage={
              form.formState.errors.endpointGlobs
                ? t('validationError.endpointGlobsRequired')
                : undefined
            }
          />
        ) : null}

        {showDescription ? (
          <Field.Root w="full" invalid={!!form.formState.errors.description}>
            <Field.Label>{t('form.description')}</Field.Label>
            <Field.HelperText>{t('form.descriptionHelper')}</Field.HelperText>
            <Textarea
              {...form.register('description')}
              placeholder={t('form.descriptionPlaceholder')}
              rows={3}
              resize="vertical"
              size="sm"
              borderColor={
                form.formState.errors.description ? 'border.error' : undefined
              }
            />
            {form.formState.errors.description ? (
              <Field.ErrorText>
                {t('validationError.descriptionRequired')}
              </Field.ErrorText>
            ) : null}
          </Field.Root>
        ) : null}

        <Field.Root
          w="full"
          invalid={!!form.formState.errors.content || !!contentError}
        >
          <Field.Label>{t('form.content')}</Field.Label>
          <Field.HelperText>{t('form.contentHelper')}</Field.HelperText>
          <Textarea
            {...form.register('content')}
            placeholder={t('form.contentPlaceholder')}
            rows={5}
            resize="vertical"
            size="sm"
            borderColor={
              form.formState.errors.content || contentError
                ? 'border.error'
                : undefined
            }
          />
          {form.formState.errors.content ? (
            <Field.ErrorText>
              {t('validationError.contentRequired')}
            </Field.ErrorText>
          ) : null}
          {contentError ? (
            <Field.ErrorText>{contentError}</Field.ErrorText>
          ) : null}
        </Field.Root>

        <Field.Root w="full">
          <Field.Label>{t('form.category')}</Field.Label>
          <Controller
            control={form.control}
            name="category"
            render={({ field }) => (
              <Select.Root
                collection={categoryOptions}
                value={field.value ? [field.value] : []}
                onValueChange={(details) => {
                  const next = details.value[0];

                  if (!next) {
                    field.onChange(null);

                    return;
                  }

                  const parsed = InstructionCategorySchema.safeParse(next);

                  field.onChange(parsed.success ? parsed.data : null);
                }}
                size="sm"
                positioning={{ sameWidth: true }}
              >
                <Select.Trigger>
                  <Select.ValueText placeholder={t('form.categoryOptional')} />
                  <ArrowUpDownIcon />
                </Select.Trigger>
                <Select.Positioner>
                  <Select.Content>
                    {categoryOptions.items.map((item) => (
                      <Select.Item key={item.value} item={item}>
                        <Select.ItemText>{item.label}</Select.ItemText>
                        <Select.ItemIndicator>
                          <Tick01Icon />
                        </Select.ItemIndicator>
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Positioner>
              </Select.Root>
            )}
          />
        </Field.Root>
      </VStack>
    </Dialog>
  );
};
